import postsData from '../../blog/posts.json'

// Virtual filesystem structure for autocomplete
export interface FileSystemNode {
  name: string
  type: 'file' | 'directory'
  hidden?: boolean
  children?: FileSystemNode[]
}

// Generate blog post file nodes dynamically from posts.json
const blogPostNodes: FileSystemNode[] = postsData.map(post => ({
  name: `${post.slug}.md`,
  type: 'file' as const
}))

export const virtualFilesystem: FileSystemNode = {
  name: '~',
  type: 'directory',
  children: [
    { name: '.theme', type: 'file', hidden: true },
    { name: '.secrets', type: 'file', hidden: true },
    { name: 'about.txt', type: 'file' },
    { name: 'contact.txt', type: 'file' },
    { name: 'README.md', type: 'file' },
    {
      name: 'projects',
      type: 'directory',
      children: [
        { name: 'YAPS', type: 'directory' },
        { name: 'jott', type: 'directory' },
        { name: 'jobsearch-cli', type: 'directory' },
        { name: 'tts-extension', type: 'directory' },
        { name: 'interviewer', type: 'directory' },
        { name: 'Fact-Checker', type: 'directory' },
        { name: 'FirstResponder', type: 'directory' },
        { name: 'StoryTimeStar', type: 'directory' },
        { name: 'therapissed', type: 'directory' },
        { name: 'Ideanas', type: 'directory' },
        { name: 'rubber-ducky-live', type: 'directory' },
      ],
    },
    {
      name: 'skills',
      type: 'directory',
      children: [
        { name: 'languages.txt', type: 'file' },
        { name: 'specializations.txt', type: 'file' },
        { name: 'expertise', type: 'directory' },
      ],
    },
    {
      name: 'experience',
      type: 'directory',
      children: [
        { name: '2024-present', type: 'directory' },
        { name: '2020-2024', type: 'directory' },
        { name: '2015-2020', type: 'directory' },
        { name: 'achievements.txt', type: 'file' },
        { name: 'expertise.txt', type: 'file' },
      ],
    },
    {
      name: 'thoughts',
      type: 'directory',
      children: blogPostNodes,
    },
    {
      name: 'samples',
      type: 'directory',
      children: [
        { name: 'sample.json', type: 'file' },
        { name: 'sample.md', type: 'file' },
        { name: 'sample.js', type: 'file' },
        { name: 'sample.sh', type: 'file' },
      ],
    },
  ],
}

/**
 * Get all files and directories at the root level
 */
export function getRootEntries(showHidden = false): string[] {
  const children = virtualFilesystem.children || []
  if (showHidden) {
    return children.map((node) => node.name)
  }
  return children.filter((node) => !node.hidden).map((node) => node.name)
}

/**
 * Get entries for a specific directory
 */
export function getDirectoryEntries(path: string, showHidden = false): string[] {
  const normalizedPath = path.replace(/^~\//, '').replace(/\/$/, '')

  if (!normalizedPath || path === '~' || path === '/') {
    return getRootEntries(showHidden)
  }

  const parts = normalizedPath.split('/')
  let current = virtualFilesystem

  for (const part of parts) {
    const child = current.children?.find((node) => node.name === part)
    if (!child || child.type !== 'directory') {
      return []
    }
    current = child
  }

  const children = current.children || []
  if (showHidden) {
    return children.map((node) => node.name)
  }
  return children.filter((node) => !node.hidden).map((node) => node.name)
}

/**
 * Get autocomplete suggestions for a partial path
 * @param partial - The partial path to complete
 * @param currentDirectory - The current working directory (default: '~')
 * @param showHidden - Whether to include hidden files (default: false)
 */
export function getPathSuggestions(
  partial: string,
  currentDirectory: string = '~',
  showHidden: boolean = false
): string[] {
  // Handle empty input - return entries from current directory
  if (!partial) {
    const normalizedCurrent = currentDirectory.replace(/^~\//, '')
    return normalizedCurrent === '~' || normalizedCurrent === ''
      ? getRootEntries(showHidden)
      : getDirectoryEntries(normalizedCurrent, showHidden)
  }

  // Track whether the original input was relative or absolute
  const isAbsolutePath = partial.startsWith('~/') || partial.startsWith('/')
  const isRelativePath = !isAbsolutePath && currentDirectory !== '~'

  // Resolve the partial path relative to current directory
  let resolvedPartial = partial

  // Handle absolute paths (starting with ~/ or /)
  if (partial.startsWith('~/') || partial.startsWith('/')) {
    resolvedPartial = partial.replace(/^~\//, '').replace(/^\//, '')
  }
  // Handle parent directory navigation
  else if (partial.startsWith('../') || partial === '..') {
    // For now, keep as-is and let the splitting logic handle it
    // This would need more complex path resolution
  }
  // Handle current directory reference
  else if (partial.startsWith('./')) {
    resolvedPartial = partial.substring(2)
    if (currentDirectory !== '~') {
      const normalizedCurrent = currentDirectory.replace(/^~\//, '')
      resolvedPartial = normalizedCurrent ? `${normalizedCurrent}/${resolvedPartial}` : resolvedPartial
    }
  }
  // Relative path - append to current directory
  else if (currentDirectory !== '~') {
    const normalizedCurrent = currentDirectory.replace(/^~\//, '')
    resolvedPartial = normalizedCurrent ? `${normalizedCurrent}/${partial}` : partial
  }

  // Split path into directory and filename parts
  const lastSlash = resolvedPartial.lastIndexOf('/')
  const dirPath = lastSlash >= 0 ? resolvedPartial.substring(0, lastSlash) : ''
  const filePrefix = lastSlash >= 0 ? resolvedPartial.substring(lastSlash + 1) : resolvedPartial

  // Get entries from the appropriate directory
  const entries = dirPath
    ? getDirectoryEntries(dirPath, showHidden)
    : currentDirectory === '~' || currentDirectory === ''
    ? getRootEntries(showHidden)
    : getDirectoryEntries(currentDirectory.replace(/^~\//, ''), showHidden)

  // Filter by prefix
  const matches = entries.filter((name) => name.startsWith(filePrefix))

  // For relative paths in subdirectories, return just the filename
  // For absolute paths or paths with slashes, return the full resolved path
  if (isRelativePath && !partial.includes('/')) {
    // Simple relative path like "Y" when in ~/projects -> return just "YAPS"
    return matches
  }

  // If there's a directory path in the result, include it
  if (dirPath) {
    return matches.map((name) => `${dirPath}/${name}`)
  }

  return matches
}

/**
 * Check if a node is a directory
 */
export function isDirectory(path: string): boolean {
  const normalizedPath = path.replace(/^~\//, '').replace(/\/$/, '')

  if (!normalizedPath || path === '~' || path === '/') {
    return true
  }

  const parts = normalizedPath.split('/')
  let current = virtualFilesystem

  for (const part of parts) {
    const child = current.children?.find((node) => node.name === part)
    if (!child) {
      return false
    }
    if (child.type === 'directory') {
      current = child
    } else {
      return false
    }
  }

  return true
}
